import { FC, ReactElement, ReactNode, useEffect, useState, lazy, Suspense } from 'react';

import classNames from 'classnames';
import { useFormik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { IExerciseEditViewProps, IExerciseForm } from './exercise-edit-view.interface';
import styles from './exercise-edit-view.module.scss';

import { reportError } from 'lib/reportError';
import { KnowledgeAreaLevelExercisesApi } from 'services';
import { getAxiosErrorMessage } from 'utils';
import { getExerciseCategoryId } from 'utils/getExerciseCategoryId';
import {
  AttachmentUpload,
  Bookmark,
  Button,
  Lightning,
  Hint,
  Input,
  Select,
  Text,
} from 'views/components';
import {
  ExerciseCategoryName,
  IExerciseCategoryNames,
  IExerciseRequest,
} from 'views/components/Exercise/exercise.interface';
import { IAttachment } from 'views/components/UI/AttachmentUpload';
import {
  DialogFooter,
  useDialogBusy,
  DialogForm,
  DialogStack,
} from 'views/components/UI/Modal';

const ExerciseDescriptionEditor = lazy(() =>
  import('views/pages/KnowledgeArea/ExerciseDetails/ExerciseDescriptionEditor').then(
    module => ({ default: module.ExerciseDescriptionEditor }),
  ),
);

const ExerciseEditView: FC<IExerciseEditViewProps> = (props): ReactElement => {
  const {
    currentExercise,
    levelId,
    refetchExercises,
    updateKnowledgeAreaDetails,
    onClose,
  } = props;
  const intl = useIntl();

  const [exCategories, setExCategories] = useState<IExerciseCategoryNames | []>([]);

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  useDialogBusy(isLoading);
  const [hasBlockingAttachments, setHasBlockingAttachments] = useState(false);
  const [newAttachments, setNewAttachments] = useState<IAttachment[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [attachmentSubmitStatus, setAttachmentSubmitStatus] = useState<
    'scanning' | 'rejected' | undefined
  >();

  const id = currentExercise?.id;
  const name = currentExercise ? currentExercise.name : '';
  const category = currentExercise ? currentExercise.exCategory.name : '';
  const description = currentExercise ? currentExercise.description : '';
  const existingAttachments = (currentExercise?.attachments ?? []).filter(
    attachment => !removedAttachmentIds.includes(attachment.id),
  );

  const handleAttachmentsChange = (attachments: IAttachment[]): void => {
    setNewAttachments(attachments);
    setAttachmentSubmitStatus(undefined);
  };

  const MAX_DESCRIPTION_LENGTH = 2000;
  const MAX_NAME_LENGTH = 100;

  async function onSaveClick(exerciseData: IExerciseForm): Promise<void> {
    const readyAttachments = newAttachments
      .filter(attachment => attachment.status === 'ready' && attachment.storageKey)
      .map(attachment => ({
        storageKey: attachment.storageKey!,
        fileName: attachment.name,
      }));

    try {
      setIsLoading(true);
      setErrorMessage('');
      if (readyAttachments.length > 0) {
        setAttachmentSubmitStatus('scanning');
      }

      const trimmedDetailsData = {
        name: exerciseData.name.trim(),
        description: exerciseData.description.trim(),
        categoryId: getExerciseCategoryId(exerciseData.category),
        levelId,
      } as IExerciseRequest;

      if (id) {
        await new KnowledgeAreaLevelExercisesApi().update(id, {
          ...trimmedDetailsData,
          retainedAttachmentIds: existingAttachments.map(attachment => attachment.id),
          attachments: readyAttachments,
        });
      } else {
        await new KnowledgeAreaLevelExercisesApi().create({
          ...trimmedDetailsData,
          attachments: readyAttachments,
        });
      }

      await refetchExercises();
      onClose();
      updateKnowledgeAreaDetails();
    } catch (err) {
      setErrorMessage(
        getAxiosErrorMessage(
          err,
          intl.formatMessage({ id: 'knowledge-area.exercise.modal.error.generic' }),
        ),
      );
      if (readyAttachments.length > 0) {
        setAttachmentSubmitStatus('rejected');
      }
    } finally {
      setIsLoading(false);
    }
  }

  const formik = useFormik({
    initialValues: {
      name,
      description,
      category,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .max(MAX_NAME_LENGTH)
        .required('knowledge-area.exercise.modal.name.error'),
      description: Yup.string().required(
        'knowledge-area.exercise.modal.description.error',
      ),
    }),
    onSubmit: onSaveClick,
  });

  async function getExerciseCategories(): Promise<void> {
    try {
      const categoryNames =
        await new KnowledgeAreaLevelExercisesApi().getExercisesCategories();

      if (!id) {
        const theoryCategory = categoryNames.filter(
          categoryName => categoryName === ExerciseCategoryName.THEORY,
        )[0];
        await formik.setFieldValue('category', theoryCategory);
      }

      setExCategories(categoryNames);
    } catch (err) {
      reportError(err, 'ExerciseEditView.ExerciseEditView', true);
    }
  }

  useEffect(() => {
    getExerciseCategories();
  }, []);

  const onFormFocus = (): void => setErrorMessage('');

  const isInputNameError = Boolean(formik.touched.name && formik.errors.name);
  const isInputDescriptionError =
    Boolean(formik.touched.description && formik.errors.description) ||
    formik.values.description.length > MAX_DESCRIPTION_LENGTH;
  const shouldShowErrorMessage =
    isInputNameError || isInputDescriptionError || Boolean(errorMessage);
  const hasAttachmentChange =
    newAttachments.some(attachment => attachment.status === 'ready') ||
    removedAttachmentIds.length > 0;
  const isSaveButtonDisabled =
    shouldShowErrorMessage ||
    isLoading ||
    hasBlockingAttachments ||
    !((formik.dirty || hasAttachmentChange) && formik.isValid);
  const descriptionLabel = (
    <>
      <FormattedMessage id="knowledge-area.exercise.modal.description.label" />
      {` (${formik.values.description.length}/${MAX_DESCRIPTION_LENGTH})`}
    </>
  );

  function renderCategoryOptionIcon(categoryName: unknown): ReactNode {
    const isPractice = categoryName === ExerciseCategoryName.PRACTICE;
    const CategoryIcon = isPractice ? Lightning : Bookmark;

    return (
      <span
        className={classNames(styles.categoryIcon, {
          [styles['categoryIcon--practice']]: isPractice,
        })}
      >
        <CategoryIcon size="16px" color="currentColor" />
      </span>
    );
  }

  return (
    <DialogForm
      onSubmit={formik.handleSubmit}
      onFocus={errorMessage ? onFormFocus : undefined}
    >
      <DialogStack>
        <Input
          label="knowledge-area.exercise.modal.name.label"
          placeholder="knowledge-area.exercise.modal.name.placeholder"
          error={isInputNameError}
          {...formik.getFieldProps('name')}
        />
        <Select
          className={styles.categorySelect}
          label="knowledge-area.exercise.modal.type.label"
          options={exCategories}
          hasIcon
          getOptionIcon={renderCategoryOptionIcon}
          {...formik.getFieldProps('category')}
          onChange={value => formik.setFieldValue('category', value)}
        />
        <Suspense
          fallback={
            <Text>
              <FormattedMessage id="exercise.editor.loading" />
            </Text>
          }
        >
          <ExerciseDescriptionEditor
            key={`${id || 'new'}-description`}
            labelContent={descriptionLabel}
            error={isInputDescriptionError}
            height="120px"
            value={formik.values.description}
            onChange={value => {
              formik.setFieldValue('description', value);
            }}
            onBlur={() => {
              formik.setFieldTouched('description', true);
            }}
          />
        </Suspense>
        <AttachmentUpload
          key={`${id || 'new'}-attachments`}
          label="knowledge-area.exercise.modal.attachments.label"
          hint="knowledge-area.exercise.modal.attachments.hint"
          requestUploadUrl={(fileName, contentType) =>
            new KnowledgeAreaLevelExercisesApi().createAttachmentUploadUrl(
              fileName,
              contentType,
            )
          }
          deletePendingUpload={storageKey =>
            new KnowledgeAreaLevelExercisesApi().deletePendingAttachment(storageKey)
          }
          onBlockingStateChange={setHasBlockingAttachments}
          onAttachmentsChange={handleAttachmentsChange}
          submitStatus={attachmentSubmitStatus}
          existingAttachments={existingAttachments.map(attachment => ({
            id: attachment.id,
            name: attachment.fileName,
            size: attachment.sizeBytes,
            contentType: attachment.contentType,
            requestDownloadUrl: () =>
              new KnowledgeAreaLevelExercisesApi()
                .getAttachmentDownloadUrl(attachment.id)
                .then(response => response.downloadUrl),
            onRemove: () =>
              setRemovedAttachmentIds(current => [...current, attachment.id]),
          }))}
        />
        {shouldShowErrorMessage && (
          <Hint>
            {isInputNameError && (
              <Text variant="bodySmall" color="inherit">
                <FormattedMessage id={formik.errors.name} />
              </Text>
            )}

            {isInputDescriptionError && (
              <Text variant="bodySmall" color="inherit">
                <FormattedMessage
                  id={
                    formik.errors.description ||
                    'knowledge-area.exercise.modal.description.error-length'
                  }
                />
              </Text>
            )}

            {errorMessage && (
              <Text variant="bodySmall" color="inherit">
                {errorMessage}
              </Text>
            )}
          </Hint>
        )}
      </DialogStack>
      <DialogFooter>
        <Button disabled={isSaveButtonDisabled} type="submit">
          <FormattedMessage id="knowledge-area.exercise.modal.button.save" />
        </Button>
      </DialogFooter>
    </DialogForm>
  );
};

export { ExerciseEditView };
